import type { OnboardingStatus } from '@/hooks/use-onboarding';
import type { ClientChatSuggestionsConfig, ComposerStarter } from '@/types/composer';
import {
  countedOnboardingSteps,
  employerAccountOnboardingSteps,
} from '@/lib/onboarding-display';
import { EMPLOYER_ORG_SETUP_STEP_IDS } from '@/lib/employer-org-setup';
import { firstIncompleteCountedStepId } from '@/lib/onboarding-setup-list-copy';

const LABEL_MAX = 40;

function resolveCurrentOnboardingStepId(
  status: OnboardingStatus,
  completedIds: string[],
): string | null {
  const allSteps = status.steps ?? [];
  const accountSteps = employerAccountOnboardingSteps(allSteps, status.group_name);
  const accountCounted = countedOnboardingSteps(accountSteps);
  const accountPending = firstIncompleteCountedStepId(accountCounted, completedIds);
  if (accountPending) return accountPending;

  if (status.group_name !== 'employer' || !completedIds.includes('organisation_setup')) {
    return firstIncompleteCountedStepId(accountCounted, completedIds);
  }

  const orgPending = EMPLOYER_ORG_SETUP_STEP_IDS.find(
    (id) => allSteps.some((step) => step.id === id) && !completedIds.includes(id),
  );
  return orgPending ?? null;
}

function truncateLabel(text: string): string {
  const t = text.trim();
  if (t.length <= LABEL_MAX) return t;
  return `${t.slice(0, LABEL_MAX - 1)}…`;
}

export interface OnboardingComposerConfig {
  heroGreeting?: string;
  heroSubline?: string;
  placeholder?: string;
  startersByStep?: Record<string, string[]>;
}

export interface ResolvedOnboardingComposer {
  active: boolean;
  greeting: string | null;
  subline: string | null;
  placeholder: string | null;
  starters: ComposerStarter[];
  currentStepId: string | null;
}

function toStarters(messages: string[]): ComposerStarter[] {
  return messages
    .map((m) => m.trim())
    .filter(Boolean)
    .map((message) => ({
      label: truncateLabel(message),
      message,
    }));
}

export function resolveOnboardingComposer(
  status: OnboardingStatus | null | undefined,
  chatSuggestions: ClientChatSuggestionsConfig | undefined,
  focusKey: string | null | undefined,
  starterCount: number,
): ResolvedOnboardingComposer {
  const inactive: ResolvedOnboardingComposer = {
    active: false,
    greeting: null,
    subline: null,
    placeholder: null,
    starters: [],
    currentStepId: null,
  };

  if (!status?.has_onboarding || status.is_completed) {
    return inactive;
  }

  const focus = focusKey?.trim() || status.group_name || 'default';
  const onboardingRoot = chatSuggestions?.onboarding?.[focus]
    ?? chatSuggestions?.onboarding?.default;

  if (!onboardingRoot) {
    return inactive;
  }

  const completedIds = status.completed_step_ids ?? [];
  const currentStepId = resolveCurrentOnboardingStepId(status, completedIds) ?? 'default';

  const stepStarters =
    onboardingRoot.startersByStep?.[currentStepId]
    ?? onboardingRoot.startersByStep?.default
    ?? [];

  return {
    active: true,
    greeting: onboardingRoot.heroGreeting ?? "Let's finish your setup",
    subline:
      onboardingRoot.heroSubline
      ?? 'Continue in the wizard, or ask me to help with any step.',
    placeholder: onboardingRoot.placeholder ?? 'Ask me to help with setup…',
    starters: toStarters(stepStarters).slice(0, starterCount),
    currentStepId,
  };
}

export function mergeComposerStartersWithOnboarding(
  focusStarters: ComposerStarter[],
  onboarding: ResolvedOnboardingComposer,
): ComposerStarter[] {
  if (!onboarding.active || onboarding.starters.length === 0) {
    return focusStarters;
  }
  return onboarding.starters;
}
